import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getProductPromoCodes, 
  getPromoCode,
  createPromoCode,
  updatePromoCode,
  deletePromoCode,
  trackPromoCodeClick,
  getAllPromoCodes,
} from '@/data/promoCodes';
import { PromoCodeInsert, PromoCodeUpdate } from '@/contracts/promoCodes';
import { toast } from 'sonner';

/**
 * Hook for fetching promo codes for a product
 * Aggressive caching (5 min) as promo codes change infrequently
 */
export function useProductPromoCodes(productId: string) {
  return useQuery({
    queryKey: ['promo-codes', productId],
    queryFn: () => getProductPromoCodes(productId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!productId,
  });
}

/**
 * Hook for fetching single promo code
 */
export function usePromoCode(promoId: string) {
  return useQuery({
    queryKey: ['promo-code', promoId],
    queryFn: () => getPromoCode(promoId),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!promoId,
  });
}

/**
 * Hook for fetching all promo codes (admin)
 */
export function useAllPromoCodes() {
  return useQuery({
    queryKey: ['promo-codes', 'all'],
    queryFn: getAllPromoCodes,
    staleTime: 2 * 60 * 1000, // 2 minutes for admin
    gcTime: 10 * 60 * 1000,
    refetchOnMount: true, // Admins want fresh data
  });
}

/**
 * Hook for creating promo code
 */
export function useCreatePromoCode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (promo: PromoCodeInsert) => createPromoCode(promo),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['promo-codes', data.product_id] });
      queryClient.invalidateQueries({ queryKey: ['promo-codes', 'all'] });
      toast.success('Promo code created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create promo code: ${error.message}`);
    },
  });
}

/**
 * Hook for updating promo code
 */
export function useUpdatePromoCode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ promoId, updates }: { promoId: string; updates: PromoCodeUpdate }) => 
      updatePromoCode(promoId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-codes'] });
      toast.success('Promo code updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update promo code: ${error.message}`);
    },
  });
}

/**
 * Hook for deleting promo code
 */
export function useDeletePromoCode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (promoId: string) => deletePromoCode(promoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-codes'] });
      toast.success('Promo code deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete promo code: ${error.message}`);
    },
  });
}

/**
 * Hook for tracking promo code click
 */
export function useTrackPromoClick() {
  return useMutation({
    mutationFn: (promoId: string) => trackPromoCodeClick(promoId),
    // Silent - no toast needed for tracking
  });
}
